import * as React from "react";
import CriterionCard from "./CriterionCard";
import CategoryPanel from "../commons/components/CategoryPanel";
import Report, {
  AssessmentResultObservationCategoryModel,
  getCriteriaByCategory,
} from "../commons/model/report/Report";
import AnalysedCriterion from "../commons/model/treatment/AnalysedCriterion";
import Photo from "../commons/model/Photo";
import PatientAnswers from "../commons/components/PatientAnswers";
import { sanitize } from "../commons/utils/sanitize";

interface AssessmentResultObservationsProps {
  report: Report;
  onSelectPhoto?: (photo: Photo) => void;
  printMode?: boolean;
  goToTreatmentSelection?: (criterion: AnalysedCriterion) => any;
  criterionUpdatable: boolean;
  getSeverityAssessmentRulesAction?: GetSeverityAssessmentRulesAction;
  useHash?: boolean;
  isAuthenticated?: boolean;
}

interface AssessmentResultObservationsState {
  selectedCriterion?: AnalysedCriterion;
  criteriaByCategory: AssessmentResultObservationCategoryModel[];
  bottomPanelCriteria: AnalysedCriterion[];
}

export interface GetSeverityAssessmentRulesAction {
  url: string;
}

export default class AssessmentResultObservations extends React.Component<
  AssessmentResultObservationsProps,
  AssessmentResultObservationsState
> {
  constructor(props: AssessmentResultObservationsProps) {
    super(props);
    const criteriaByCategory = getCriteriaByCategory(this.props.report);
    const bottomPanelCriteria = this.getIdentifiableCriteriaForBottomPanel(criteriaByCategory);
    const selectedCriterion = this.getSelectedCriterion(bottomPanelCriteria);
    this.state = {
      selectedCriterion,
      criteriaByCategory,
      bottomPanelCriteria,
    };
  }

  render() {
    return (
      <>
        {this.props.printMode && (
          <div className="printable-title">{i18n["Page.AssessmentResult.Tab.Observations"]}</div>
        )}
        <div className="group">
          {this.props.report.patientAnswers.length ? (
            <PatientAnswers answers={this.props.report.patientAnswers} />
          ) : null}

          {!this.props.printMode && this.props.getSeverityAssessmentRulesAction && (
            <div className="message info">
              <span>
                {i18n["Page.AssessmentResult.Observations.SeverityAssessmentRule.Panel"]}
                <a
                  href={this.props.getSeverityAssessmentRulesAction.url}
                  className="link"
                  target="_blank"
                >
                  {i18n["Page.AssessmentResult.Observations.SeverityAssessmentRule.Link"]}
                </a>
                .
              </span>
            </div>
          )}
          <div>
            {this.state.criteriaByCategory.map((observationCategory) => (
              <CategoryPanel
                key={observationCategory.id}
                id={observationCategory.id}
                label={observationCategory.label}
              >
                <div className="grid">
                  {observationCategory.criteria.map((criterion) => (
                    <CriterionCard
                      key={criterion.criterionId}
                      criterion={criterion}
                      onSelect={
                        !this.props.printMode &&
                        ((criterion, ref) => {
                          const timeout = this.state.selectedCriterion ? 0 : 300;
                          this.setState({ selectedCriterion: criterion });
                          window.setTimeout(
                            () =>
                              ref.current.scrollIntoView({
                                block: "center",
                                behavior: "smooth",
                              }),
                            timeout,
                          );
                        })
                      }
                      selected={
                        this.state.selectedCriterion &&
                        criterion.criterionId === this.state.selectedCriterion.criterionId
                      }
                      printMode={this.props.printMode}
                      updatable={false}
                    />
                  ))}
                </div>
              </CategoryPanel>
            ))}
          </div>

          <div
            className={"bottom-panel-spacer" + (this.state.selectedCriterion ? " active" : "")}
          />
          <div
            className={
              "bottom-panel group" +
              (this.state.selectedCriterion ? " active" : "") +
              (this.props.isAuthenticated ? " with-navigation" : "")
            }
          >
            <div className="panel-control">
              <i
                className="ic_close"
                onClick={() => this.setState({ selectedCriterion: undefined })}
              />
            </div>
            {this.state.selectedCriterion && (
              <div className="container" style={{ display: "block" }}>
                <div className="previous-arrow" onClick={() => this.previousCriterion()}>
                  <i className="ic_chevron_left" />
                </div>
                <div className="next-arrow" onClick={() => this.nextCriterion()}>
                  <i className="ic_chevron_right" />
                </div>
                <div
                  className={
                    !this.state.selectedCriterion.contentPictureUrl ? "" : "inner-container"
                  }
                >
                  <CriterionCard
                    criterion={this.state.selectedCriterion}
                    updatable={this.props.criterionUpdatable}
                  />
                  <div>&nbsp;</div>

                  <div>
                    {this.state.selectedCriterion.contentPictureUrl && (
                      <div className="picture">
                        <img
                          src={this.state.selectedCriterion.contentPictureUrl}
                          alt={this.state.selectedCriterion.criterionLabel}
                        />
                      </div>
                    )}
                  </div>
                  <div
                    className={
                      this.state.selectedCriterion.contentPictureUrl ||
                      this.state.selectedCriterion.mouthPhotos.length > 0
                        ? ""
                        : "full-width"
                    }
                  >
                    <div id="selected-criterion-what">
                      {this.state.selectedCriterion.contentPictureUrl && (
                        <p className="subtitle no-top-margin">
                          {i18n["Page.AssessmentResult.Observations.What"]}
                        </p>
                      )}
                      <p
                        className="content"
                        dangerouslySetInnerHTML={{
                          __html: sanitize(this.state.selectedCriterion.contentDescriptionLabel),
                        }}
                      />
                    </div>

                    {this.state.selectedCriterion.contentRiskLabel && (
                      <div id="selected-criterion-why">
                        <p className="subtitle">{i18n["Page.AssessmentResult.Observations.Why"]}</p>
                        <p
                          className="content"
                          dangerouslySetInnerHTML={{
                            __html: sanitize(this.state.selectedCriterion.contentRiskLabel),
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
                <div>
                  {this.isCriterionTreated(this.state.selectedCriterion) && (
                    <div className="text-align-center">
                      <button
                        className="button-primary go-to-treatments"
                        onClick={() =>
                          this.props.goToTreatmentSelection(this.state.selectedCriterion)
                        }
                      >
                        {i18n["Page.AssessmentResult.Action.GoToTreatmentOptions"]}
                      </button>
                    </div>
                  )}

                  <div className="separator" />

                  <div
                    id="selected-criterion-photos"
                    className="h-flex with-margins balanced mobile-block"
                  >
                    {this.state.selectedCriterion.mouthPhotos.map((photo) => (
                      <div key={photo.uid} className="emphasize-img">
                        <img
                          src={photo.href}
                          className="clickable"
                          onClick={() =>
                            this.props.onSelectPhoto && this.props.onSelectPhoto(photo)
                          }
                          alt="emphasize image"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  private getSelectedCriterion(bottomPanelCriteria: AnalysedCriterion[]) {
    const criterionHash = window.location.hash;
    let selectedCriterion = undefined;
    if (criterionHash && this.props.useHash) {
      const criterionUid = criterionHash.substring(1);
      selectedCriterion = bottomPanelCriteria.find((criterion) => criterion.uid === criterionUid);
    }
    return selectedCriterion;
  }

  private isCriterionTreated = (criterion: AnalysedCriterion) =>
    this.props.report.treatmentSelections.some((selection) =>
      selection.criteria.includes(criterion),
    );

  private getIdentifiableCriteriaForBottomPanel(
    criteriaByCategory: AssessmentResultObservationCategoryModel[],
  ): AnalysedCriterion[] {
    let criteria = [];
    criteriaByCategory.forEach((category) => {
      let tmpCriteria = [];
      category.criteria.forEach((criterion) => {
        tmpCriteria.push(criterion);
      });
      criteria = criteria.concat(tmpCriteria);
    });
    return criteria;
  }

  private nextCriterion() {
    const currentItemIndex = this.state.bottomPanelCriteria.indexOf(this.state.selectedCriterion);
    if (currentItemIndex === this.state.bottomPanelCriteria.length - 1) {
      this.setState({ selectedCriterion: this.state.bottomPanelCriteria[0] });
    } else {
      this.setState({
        selectedCriterion: this.state.bottomPanelCriteria[currentItemIndex + 1],
      });
    }
  }

  private previousCriterion() {
    let currentItemIndex = this.state.bottomPanelCriteria.indexOf(this.state.selectedCriterion);
    if (currentItemIndex === 0) {
      const nextItemIndex = this.state.bottomPanelCriteria.length - 1;
      this.setState({
        selectedCriterion: this.state.bottomPanelCriteria[nextItemIndex],
      });
    } else {
      this.setState({
        selectedCriterion: this.state.bottomPanelCriteria[currentItemIndex - 1],
      });
    }
  }
}
